import React, { forwardRef, useMemo, useRef } from 'react';
import { POSTER_SCHOOL_LOGO } from './BoardResultsPosterCard';
import {
  POSTER_SCHOOL_PHONE,
  POSTER_SCHOOL_WEBSITE
} from './BoardResultsTop5Poster';
import './BoardResultsFlyerPoster.css';

/** Physical print size: 21 ft wide × 8 ft tall @ 100 px/ft */
export const FLYER_WIDTH_FT = 21;
export const FLYER_HEIGHT_FT = 8;
export const FLYER_PX_PER_FT = 100;
export const FLYER_WIDTH_PX = FLYER_WIDTH_FT * FLYER_PX_PER_FT; // 2100
export const FLYER_HEIGHT_PX = FLYER_HEIGHT_FT * FLYER_PX_PER_FT; // 800
/** Flyer includes students with total marks at or above this (e.g. Part-I 400+). */
export const FLYER_MIN_MARKS = 400;
export const FLYER_MIN_PCT = 80;
const FLYER_THEME_KEY = 'boardResults_flyerTheme';
const FLYER_PHOTO_PLACEMENT_KEY = 'boardResults_flyerPhotoPlacement';

/** Default: crop center-top; card offset 0 */
export const DEFAULT_FLYER_PHOTO_PLACEMENT = Object.freeze({
  x: 50,
  y: 0,
  zoom: 1,
  ox: 0,
  oy: 0
});

const OFFSET_LIMIT = 220;

export function normalizeFlyerPhotoPlacement(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const x = Number(src.x);
  const y = Number(src.y);
  const zoom = Number(src.zoom);
  const ox = Number(src.ox);
  const oy = Number(src.oy);
  return {
    x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : DEFAULT_FLYER_PHOTO_PLACEMENT.x,
    y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : DEFAULT_FLYER_PHOTO_PLACEMENT.y,
    zoom: Number.isFinite(zoom) ? Math.min(2.5, Math.max(1, zoom)) : DEFAULT_FLYER_PHOTO_PLACEMENT.zoom,
    ox: Number.isFinite(ox) ? Math.min(OFFSET_LIMIT, Math.max(-OFFSET_LIMIT, Math.round(ox))) : 0,
    oy: Number.isFinite(oy) ? Math.min(OFFSET_LIMIT, Math.max(-OFFSET_LIMIT, Math.round(oy))) : 0
  };
}

export function loadFlyerPhotoPlacements() {
  try {
    const raw = JSON.parse(localStorage.getItem(FLYER_PHOTO_PLACEMENT_KEY) || '{}');
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [id, val] of Object.entries(raw)) {
      if (!id) continue;
      out[String(id)] = normalizeFlyerPhotoPlacement(val);
    }
    return out;
  } catch {
    return {};
  }
}

function isDefaultFlyerPlacement(n) {
  return (
    n.x === DEFAULT_FLYER_PHOTO_PLACEMENT.x
    && n.y === DEFAULT_FLYER_PHOTO_PLACEMENT.y
    && n.zoom === DEFAULT_FLYER_PHOTO_PLACEMENT.zoom
    && n.ox === DEFAULT_FLYER_PHOTO_PLACEMENT.ox
    && n.oy === DEFAULT_FLYER_PHOTO_PLACEMENT.oy
  );
}

export function saveFlyerPhotoPlacements(map) {
  try {
    const out = {};
    for (const [id, val] of Object.entries(map || {})) {
      if (!id) continue;
      const n = normalizeFlyerPhotoPlacement(val);
      if (!isDefaultFlyerPlacement(n)) out[String(id)] = n;
    }
    localStorage.setItem(FLYER_PHOTO_PLACEMENT_KEY, JSON.stringify(out));
  } catch { /* ignore */ }
}

export function flyerPhotoImgStyle(placement) {
  const p = normalizeFlyerPhotoPlacement(placement);
  return {
    objectPosition: `${p.x}% ${p.y}%`,
    transform: p.zoom !== 1 ? `scale(${p.zoom})` : undefined,
    transformOrigin: `${p.x}% ${p.y}%`
  };
}

export function flyerCardOffsetStyle(placement) {
  const p = normalizeFlyerPhotoPlacement(placement);
  if (!p.ox && !p.oy) return undefined;
  return { transform: `translate(${p.ox}px, ${p.oy}px)` };
}
export const FLYER_THEMES = [
  {
    key: 'midnight',
    label: 'Midnight Violet',
    swatches: ['#0b1430', '#5a1868', '#e31c2d', '#f5d34a']
  },
  {
    key: 'royal',
    label: 'Royal Blue',
    swatches: ['#061833', '#0b4da2', '#f0c94a', '#ffffff']
  },
  {
    key: 'emerald',
    label: 'Emerald Night',
    swatches: ['#052016', '#0f6b45', '#d4af37', '#ecfdf5']
  },
  {
    key: 'crimson',
    label: 'Crimson Night',
    swatches: ['#1a0608', '#7f1018', '#f5d34a', '#ffffff']
  },
  {
    key: 'ocean',
    label: 'Ocean Teal',
    swatches: ['#042f3a', '#0e7490', '#fbbf24', '#ecfeff']
  },
  {
    key: 'graphite',
    label: 'Graphite Gold',
    swatches: ['#111827', '#374151', '#d4af37', '#f9fafb']
  }
];

export function loadFlyerTheme() {
  try {
    const v = localStorage.getItem(FLYER_THEME_KEY);
    if (FLYER_THEMES.some((t) => t.key === v)) return v;
  } catch { /* ignore */ }
  return 'midnight';
}

export function saveFlyerTheme(key) {
  try {
    localStorage.setItem(FLYER_THEME_KEY, key);
  } catch { /* ignore */ }
}

export function normalizeFlyerTheme(key) {
  return FLYER_THEMES.some((t) => t.key === key) ? key : 'midnight';
}

function formatPercentage(row) {
  if (row?.percentage != null && Number.isFinite(Number(row.percentage))) {
    return `%${Math.round(Number(row.percentage))}`;
  }
  const label = String(row?.percentage_label || '').replace(/\s/g, '');
  const n = Number(label.replace('%', ''));
  if (Number.isFinite(n)) return `%${Math.round(n)}`;
  return label ? (label.startsWith('%') ? label : `%${label}`) : '';
}

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function displayName(name, compact = false) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (!compact || parts.length <= 2) return parts.join(' ').toUpperCase();
  return `${parts[0]} ${parts[parts.length - 1]}`.toUpperCase();
}

function excellentResultLabel(part, year) {
  const p = String(part || '');
  const classLabel = p === 'Part-I' ? '9th' : p === 'Part-II' ? '10th' : p || '';
  return ['Excellent Result', classLabel, year].filter(Boolean).join(' ');
}

/** Dense bottom rows — prefer ~15 per row like the print sample. */
export function flyerRestRows(rest) {
  const n = rest.length;
  if (n <= 0) return [];
  if (n <= 16) return [rest];
  if (n <= 32) {
    const cols = Math.ceil(n / 2);
    return [rest.slice(0, cols), rest.slice(cols)];
  }
  if (n <= 48) {
    const cols = Math.ceil(n / 3);
    return [rest.slice(0, cols), rest.slice(cols, cols * 2), rest.slice(cols * 2)];
  }
  const rowCount = Math.min(4, Math.ceil(n / 15));
  const cols = Math.ceil(n / rowCount);
  const rows = [];
  for (let i = 0; i < n; i += cols) rows.push(rest.slice(i, i + cols));
  return rows;
}

/**
 * Top band: brand + larger students (count scales with total — not fixed forever at 5).
 * Remaining students fill dense rows below.
 */
export function flyerLayout(list) {
  const n = list.length;
  if (n <= 0) return { featured: [], restRows: [] };
  if (n <= 8) return { featured: list, restRows: [] };

  let featuredCount = 5;
  if (n >= 60) featuredCount = 8;
  else if (n >= 52) featuredCount = 7;
  else if (n >= 40) featuredCount = 5;
  else if (n >= 25) featuredCount = 4;
  else featuredCount = 3;

  featuredCount = Math.min(featuredCount, n - 8); // keep enough for at least one dense row
  featuredCount = Math.max(3, featuredCount);

  return {
    featured: list.slice(0, featuredCount),
    restRows: flyerRestRows(list.slice(featuredCount))
  };
}

const rollLabelCache = new Map();

/** Match/exceed flyer PDF capture scale so roll labels stay sharp when html2canvas upscales. */
const ROLL_LABEL_DPR = 4;

/**
 * Paint “ROLL NO. 123456” rotated −90° (reads bottom → top) as a high-DPI PNG.
 * Avoids CSS writing-mode/transform which html2canvas scrambles on download.
 */
function rollLabelDataUrl(roll, large = false) {
  const digits = String(roll || '').replace(/\D/g, '');
  if (!digits) return null;
  const key = `${digits}:${large ? 'L' : 'S'}:d${ROLL_LABEL_DPR}`;
  if (rollLabelCache.has(key)) return rollLabelCache.get(key);

  const text = `ROLL NO. ${digits}`;
  const cssFontSize = large ? 11 : 8;
  const dpr = ROLL_LABEL_DPR;
  const fontSize = cssFontSize * dpr;
  const font = `700 ${fontSize}px Montserrat, "Segoe UI", Arial, sans-serif`;

  const probe = document.createElement('canvas').getContext('2d');
  probe.font = font;
  const textWidth = Math.ceil(probe.measureText(text).width) + 4 * dpr;

  const canvas = document.createElement('canvas');
  // After −90° rotation, canvas width = glyph height, height = text length
  canvas.width = Math.ceil(fontSize + 6 * dpr);
  canvas.height = textWidth;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(text, 0, 0);

  const url = canvas.toDataURL('image/png');
  rollLabelCache.set(key, url);
  return url;
}

function FlyerStudentCard({
  row,
  photoUrl,
  size = 'normal',
  placement,
  editable = false,
  editDragScale = 1,
  onPhotoClick,
  onPlacementChange
}) {
  const pct = formatPercentage(row);
  const marks = row?.total_marks != null ? String(row.total_marks) : '';
  const roll = row?.roll_no ? String(row.roll_no).replace(/\D/g, '') : '';
  const full = String(row?.student_name || '').trim().toUpperCase();
  const name = displayName(row?.student_name, size !== 'large');
  const large = size === 'large';
  const rollImg = useMemo(() => (roll ? rollLabelDataUrl(roll, large) : null), [roll, large]);
  const normalized = useMemo(() => normalizeFlyerPhotoPlacement(placement), [placement]);
  const imgStyle = useMemo(() => flyerPhotoImgStyle(normalized), [normalized]);
  const cardStyle = useMemo(() => flyerCardOffsetStyle(normalized), [normalized]);
  const canEdit = editable && typeof onPlacementChange === 'function';
  const dragRef = useRef(null);

  const endCardDrag = (e) => {
    const drag = dragRef.current;
    if (!drag || (e?.pointerId != null && drag.pointerId !== e.pointerId)) return;
    dragRef.current = null;
    try {
      e.currentTarget?.releasePointerCapture?.(e.pointerId);
    } catch { /* ignore */ }

    if (!drag.moved) {
      if (photoUrl && typeof onPhotoClick === 'function') onPhotoClick(row);
      return;
    }
    onPlacementChange?.(row, normalizeFlyerPhotoPlacement({
      ...normalized,
      ox: drag.ox,
      oy: drag.oy
    }));
  };

  return (
    <div
      className={`br-flyer-card is-${size}${canEdit ? ' is-editable-card' : ''}`}
      style={cardStyle}
      title={canEdit ? 'Drag to move student · click for crop & fine tune' : undefined}
      onPointerDown={canEdit ? (e) => {
        if (e.button != null && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const scale = editDragScale > 0.01 ? editDragScale : 1;
        dragRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          origOx: normalized.ox,
          origOy: normalized.oy,
          ox: normalized.ox,
          oy: normalized.oy,
          scale,
          moved: false
        };
      } : undefined}
      onPointerMove={canEdit ? (e) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        const dx = (e.clientX - drag.startX) / drag.scale;
        const dy = (e.clientY - drag.startY) / drag.scale;
        if (!drag.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) drag.moved = true;
        if (!drag.moved) return;
        const next = normalizeFlyerPhotoPlacement({
          ...normalized,
          ox: drag.origOx + dx,
          oy: drag.origOy + dy
        });
        drag.ox = next.ox;
        drag.oy = next.oy;
        e.currentTarget.style.transform = `translate(${next.ox}px, ${next.oy}px)`;
      } : undefined}
      onPointerUp={canEdit ? endCardDrag : undefined}
      onPointerCancel={canEdit ? endCardDrag : undefined}
    >
      <div className="br-flyer-photo-stack">
        {rollImg && (
          <img
            src={rollImg}
            alt=""
            className="br-flyer-roll"
            draggable={false}
          />
        )}
        <div className={`br-flyer-photo${canEdit && photoUrl ? ' is-editable' : ''}`}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              crossOrigin="anonymous"
              draggable={false}
              style={imgStyle}
            />
          ) : (
            <span className="br-flyer-photo-fallback">{initialsFromName(row?.student_name)}</span>
          )}
        </div>
      </div>
      <div className="br-flyer-caption">
        <div className="br-flyer-name" title={full}>{name}</div>
        <div className="br-flyer-scores">
          {marks && <span className="br-flyer-marks">{marks}</span>}
          {pct && <span className="br-flyer-pct">{pct}</span>}
        </div>
      </div>
    </div>
  );
}

const BoardResultsFlyerPoster = forwardRef(function BoardResultsFlyerPoster(
  {
    rows = [],
    photoUrls = {},
    photoPlacements = {},
    editingPhotos = false,
    editDragScale = 1,
    onPhotoClick,
    onPlacementChange,
    level,
    part,
    year,
    sectionLabel,
    groupLabel,
    theme = 'midnight'
  },
  ref
) {
  const list = Array.isArray(rows) ? rows : [];
  const { featured, restRows } = useMemo(() => flyerLayout(list), [list]);
  const resultLine = excellentResultLabel(part, year);
  const themeKey = normalizeFlyerTheme(theme);

  const cardProps = (row) => ({
    row,
    photoUrl: photoUrls[row.id],
    placement: photoPlacements[row.id],
    editable: editingPhotos,
    editDragScale,
    onPhotoClick,
    onPlacementChange
  });

  return (
    <div
      className={`br-poster br-poster-flyer${editingPhotos ? ' is-editing-photos' : ''}`}
      ref={ref}
      data-poster-id="flyer80"
      data-poster-width={FLYER_WIDTH_PX}
      data-poster-height={FLYER_HEIGHT_PX}
      data-theme={themeKey}
      style={{
        width: FLYER_WIDTH_PX,
        height: FLYER_HEIGHT_PX
      }}
    >
      <div className="br-flyer-bg" aria-hidden="true">
        <span className="br-flyer-glow br-flyer-glow-a" />
        <span className="br-flyer-glow br-flyer-glow-b" />
        <span className="br-flyer-glow br-flyer-glow-c" />
        <span className="br-flyer-vignette" />
      </div>

      <div className="br-flyer-body">
        <div className="br-flyer-top">
          <aside className="br-flyer-brand">
            <div className="br-flyer-brand-title">THE FAST SCHOOL &amp; FAST ACADEMY</div>
            <div className="br-flyer-brand-mid">
              <img
                src={POSTER_SCHOOL_LOGO}
                alt=""
                className="br-flyer-logo"
                crossOrigin="anonymous"
              />
              <div className="br-flyer-brand-copy">
                <div className="br-flyer-result-line">{resultLine}</div>
                <div className="br-flyer-admit">ADMISSION OPEN</div>
                <div className="br-flyer-levels">Playgroup to Matric &amp; F.Sc / I.C.S</div>
              </div>
              <img
                src={POSTER_SCHOOL_LOGO}
                alt=""
                className="br-flyer-logo"
                crossOrigin="anonymous"
              />
            </div>
          </aside>

          <div
            className="br-flyer-featured"
            style={{ ['--flyer-featured-cols']: Math.max(featured.length, 1) }}
          >
            {featured.map((row, idx) => (
              <FlyerStudentCard
                key={row.id || row.roll_no || `f-${idx}`}
                {...cardProps(row)}
                size="large"
              />
            ))}
          </div>
        </div>

        {restRows.map((rowList, rowIdx) => (
          <div
            key={`rest-${rowIdx}`}
            className="br-flyer-row"
            style={{ ['--flyer-row-cols']: Math.max(rowList.length, 1) }}
          >
            {rowList.map((row, idx) => (
              <FlyerStudentCard
                key={row.id || row.roll_no || `r-${rowIdx}-${idx}`}
                {...cardProps(row)}
                size="normal"
              />
            ))}
          </div>
        ))}
      </div>

      <footer className="br-flyer-foot">
        <span>{POSTER_SCHOOL_WEBSITE}</span>
        <span>{POSTER_SCHOOL_PHONE}</span>
      </footer>
    </div>
  );
});

export default BoardResultsFlyerPoster;
